import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import MangaCard from "@/components/feature/MangaCard";
import { mangaList } from "@/mocks/manga";

export default function MangaDetail() {
  const { id } = useParams();
  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-40 pb-24 text-center">
        <Seo title="作品不存在 - 樱花漫画" description="未找到该漫画作品。" />
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">作品不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">该作品可能已被移除或链接有误。</p>
        <Link
          to="/manga"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回漫画库
        </Link>
      </div>
    );
  }

  const related = mangaList.filter((m) => m.id !== manga.id).slice(0, 5);
  const chapterCount = Math.max(manga.chapters, 1);
  const chapters = Array.from({ length: Math.min(chapterCount, 12) }, (_, i) => chapterCount - i);

  return (
    <>
      <Seo
        title={`${manga.title}漫画_${manga.author} - 樱花漫画`}
        description={`${manga.desc} ${manga.title}是一部${manga.genre}题材的国产漫画，作者${manga.author}，本站评分${manga.rating}，已更新${manga.chapters}话。`}
        canonical={`https://comicflower.github.io/manga/${manga.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: manga.title,
          author: { "@type": "Person", name: manga.author },
          genre: manga.genre,
          inLanguage: "zh-CN",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: manga.rating,
            ratingCount: "3200",
          },
          description: manga.desc,
          numberOfPages: manga.chapters,
        }}
      />
      <div className="relative overflow-hidden bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
          <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/manga" className="hover:text-primary-700">漫画库</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">{manga.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 pb-10 lg:grid-cols-[320px_1fr]">
            <div>
              <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft">
                <img src={manga.cover} alt={`${manga.title}漫画封面`} className="w-full object-cover" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-star-line text-sm" aria-hidden="true"></i>
                  收藏作品
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-600"
                >
                  <i className="ri-notification-3-line text-sm" aria-hidden="true"></i>
                  追更提醒
                </Link>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                  {manga.title}
                </h1>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                    manga.status === "已完结" ? "bg-secondary-600" : "bg-primary-500"
                  }`}
                >
                  {manga.status}
                </span>
              </div>
              <p className="mt-2 text-sm text-foreground-600">
                作者：{manga.author} · 题材：{manga.genre}
              </p>

              <div className="mt-6 flex flex-wrap gap-4">
                <div className="rounded-xl border border-background-200 bg-background-50 px-5 py-3">
                  <p className="text-xs text-foreground-500">本站评分</p>
                  <p className="mt-1 flex items-center gap-1 font-heading text-xl font-bold text-accent-600">
                    <i className="ri-star-fill text-base" aria-hidden="true"></i>
                    {manga.rating}
                  </p>
                </div>
                <div className="rounded-xl border border-background-200 bg-background-50 px-5 py-3">
                  <p className="text-xs text-foreground-500">已更新</p>
                  <p className="mt-1 font-heading text-xl font-bold text-foreground-950">{manga.chapters} 话</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-background-50 px-5 py-3">
                  <p className="text-xs text-foreground-500">关注人数</p>
                  <p className="mt-1 font-heading text-xl font-bold text-foreground-950">{manga.followers}</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-background-50 px-5 py-3">
                  <p className="text-xs text-foreground-500">总点击</p>
                  <p className="mt-1 font-heading text-xl font-bold text-foreground-950">{manga.views}</p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">作品简介</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">{manga.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {manga.tags.map((t) => (
                    <span key={t} className="whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-background-200 bg-background-50 p-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">章节列表</h2>
                  <span className="text-xs text-foreground-500">最近 {chapters.length} 话</span>
                </div>
                <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {chapters.map((c) => (
                    <li key={c}>
                      <Link
                        to="/contact"
                        className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50"
                      >
                        <span className="truncate">第{c}话</span>
                        <i className="ri-book-open-line text-primary-600" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">你可能还喜欢</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {related.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </div>
    </>
  );
}